<template>
  <div class="building-overview-wrapper" ref="wrapperRef">
    <!-- Elegant Loading Overlay -->
    <transition name="fade">
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-content">
          <div class="loading-icon">
            <v-icon size="48" color="primary">mdi-office-building</v-icon>
            <div class="spinner"></div>
          </div>
          <p class="loading-text">Loading Building...</p>
        </div>
      </div>
    </transition>

    <!-- SVG Container -->
    <div class="svg-viewport" ref="viewportRef">
      <object 
        ref="svgObjectRef"
        type="image/svg+xml" 
        :data="svgPath" 
        @load="handleSvgLoad" 
        class="svg-content"
        :class="{ ready: !isLoading }"
      />
    </div>

    <!-- Floating Controls -->
    <transition name="slide-up">
      <div v-if="!isLoading" class="floating-controls">
        <button @click="handleZoomIn" class="control-btn" :class="{ active: zoomInActive }">
          <v-icon size="18">mdi-plus</v-icon>
        </button>
        <button @click="handleZoomOut" class="control-btn" :class="{ active: zoomOutActive }">
          <v-icon size="18">mdi-minus</v-icon>
        </button>
        <button @click="handleReset" class="control-btn reset-btn" :class="{ active: resetActive }">
          <v-icon size="18">mdi-restore</v-icon>
        </button>
      </div>
    </transition>

    <!-- Floor Info Card -->
    <transition name="info-slide">
      <div v-if="selectedFloor" class="floor-info-card">
        <div class="info-header">
          <h3>{{ getFloorName(selectedFloor) }}</h3>
          <button @click="clearSelection" class="info-close">
            <v-icon size="16">mdi-close</v-icon>
          </button>
        </div>
        <div class="info-divider"></div>
        <div class="info-content">
          <div class="info-row total">
            <span class="label">Total Rooms</span>
            <span class="value">{{ getTotalRooms(selectedFloor) }}</span>
          </div>
          <div v-for="category in getFloorCategories(selectedFloor)" :key="category.id" class="info-row">
            <span class="label">{{ category.name }}</span>
            <span class="value">{{ category.pois.length }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import svgPanZoom from 'svg-pan-zoom';

// Refs
const wrapperRef = ref(null);
const viewportRef = ref(null);
const svgObjectRef = ref(null);

// State
const isLoading = ref(true);
const selectedFloor = ref(null);
const zoomInActive = ref(false);
const zoomOutActive = ref(false);
const resetActive = ref(false);

// SVG Setup
const svgPath = ref('/assets/hbc.svg');
const floorIds = ['floor1', 'floor2', 'floor3', 'floor4'];

// Pan-Zoom Instance
let panZoom = null;
let eventListeners = [];
let activeTimeouts = [];
let resizeObs = null;
let initialized = false;

// Floor Data
const floorData = [
  {
    number: '1',
    name: 'Floor 1',
    poiCategories: [
      { id: 1, name: 'Meeting Rooms', pois: Array(5).fill({}) },
      { id: 2, name: 'Offices', pois: Array(7).fill({}) },
    ],
  },
  {
    number: '2',
    name: 'Floor 2',
    poiCategories: [
      { id: 3, name: 'Meeting Rooms', pois: Array(3).fill({}) },
      { id: 4, name: 'Offices', pois: Array(9).fill({}) },
    ],
  },
  {
    number: '3',
    name: 'Floor 3',
    poiCategories: [
      { id: 5, name: 'Meeting Rooms', pois: Array(6).fill({}) },
      { id: 6, name: 'Offices', pois: Array(6).fill({}) },
    ],
  },
  {
    number: '4',
    name: 'Floor 4',
    poiCategories: [
      { id: 7, name: 'Meeting Rooms', pois: Array(4).fill({}) },
      { id: 8, name: 'Offices', pois: Array(8).fill({}) },
    ],
  },
];

// Utility: Add timeout to tracking
const addTimeout = (fn, delay) => {
  const id = setTimeout(fn, delay);
  activeTimeouts.push(id);
  return id;
};

// Utility: Clear all timeouts
const clearAllTimeouts = () => {
  activeTimeouts.forEach(id => clearTimeout(id));
  activeTimeouts = [];
};

// Utility: Wait for element dimensions
const waitForDimensions = async (element, timeout = 5000) => {
  const startTime = Date.now();
  return new Promise((resolve, reject) => {
    const check = () => {
      if (!element) {
        reject(new Error('Element not found'));
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        resolve({ width: rect.width, height: rect.height });
      } else if (Date.now() - startTime > timeout) {
        reject(new Error('Timeout waiting for dimensions'));
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });
};

// Get floor information
const getFloorName = (floorId) => {
  const number = floorId?.replace(/\D/g, '');
  const floor = floorData.find(f => f.number === number);
  return floor?.name || '';
};

const getFloorCategories = (floorId) => {
  const number = floorId?.replace(/\D/g, '');
  const floor = floorData.find(f => f.number === number);
  return floor?.poiCategories || [];
};

const getTotalRooms = (floorId) => {
  return getFloorCategories(floorId).reduce((total, cat) => total + cat.pois.length, 0);
};

// Highlight floor
const highlightFloor = (floorId) => {
  const svgObj = svgObjectRef.value;
  const svgDoc = svgObj?.contentDocument;
  if (!svgDoc) return;

  // Update floor areas visibility
  floorIds.forEach(id => {
    const area = svgDoc.getElementById(`${id}_area`);
    if (area) {
      area.style.display = id === floorId ? 'inline' : 'none';
    }

    // Update buttons
    const btn = svgDoc.getElementById(`${id}_btn`);
    if (btn) {
      const rect = btn.querySelector('rect');
      const text = btn.querySelector('text');
      if (rect) {
        rect.setAttribute('fill', id === floorId ? '#3b82f6' : 'rgba(74, 42, 42, 0.9)');
        rect.style.filter = id === floorId ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none';
      }
      if (text) {
        text.setAttribute('fill', '#fff');
      }
    }
  });

  selectedFloor.value = floorId;
};

// Clear selection
const clearSelection = () => {
  const svgObj = svgObjectRef.value;
  const svgDoc = svgObj?.contentDocument;
  if (!svgDoc) return;

  selectedFloor.value = null;

  floorIds.forEach(id => {
    const area = svgDoc.getElementById(`${id}_area`);
    if (area) {
      area.style.display = 'none';
    }

    const btn = svgDoc.getElementById(`${id}_btn`);
    if (btn) {
      const rect = btn.querySelector('rect');
      const text = btn.querySelector('text');
      if (rect) {
        rect.setAttribute('fill', 'rgba(74, 42, 42, 0.9)');
        rect.style.filter = 'none';
      }
      if (text) {
        text.setAttribute('fill', '#fff');
      }
    }
  });
};

// Create floor buttons
const createFloorButtons = (svgDoc, svgElement) => {
  floorIds.forEach(id => {
    const ellipse = svgDoc.getElementById(`${id}_btn`);
    if (!ellipse || ellipse.tagName.toLowerCase() !== 'ellipse') {
      console.warn(`Could not find ellipse ${id}_btn`);
      return;
    }

    const cx = parseFloat(ellipse.getAttribute('cx'));
    const cy = parseFloat(ellipse.getAttribute('cy'));
    const parentGroup = ellipse.closest('g');
    
    if (!parentGroup || isNaN(cx) || isNaN(cy)) {
      console.warn(`Invalid coordinates for ${id}_btn`);
      return;
    }

    // Calculate transformed coordinates
    const matrix = parentGroup.getAttribute('transform')?.match(/matrix\(([^)]+)\)/);
    let localX = cx;
    let localY = cy;
    
    if (matrix) {
      const [scaleX, , , scaleY, tx, ty] = matrix[1].split(',').map(Number);
      localX = (cx - tx) / scaleX;
      localY = (cy - ty) / scaleY;
    }

    parentGroup.removeChild(ellipse);

    // Create SVG button
    const svgBtn = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgBtn.setAttribute('id', `${id}_btn`);
    svgBtn.setAttribute('x', localX + 120);
    svgBtn.setAttribute('y', localY + 80);
    svgBtn.setAttribute('width', 150);
    svgBtn.setAttribute('height', 50);
    svgBtn.style.cursor = 'pointer';

    // Create button background
    const rect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', '150');
    rect.setAttribute('height', '50');
    rect.setAttribute('rx', '12');
    rect.setAttribute('fill', 'rgba(74, 42, 42, 0.9)');
    rect.setAttribute('stroke', 'rgba(255, 255, 255, 0.3)');
    rect.setAttribute('stroke-width', '1');
    rect.style.transition = 'all 0.3s ease';

    // Create button text
    const text = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '75');
    text.setAttribute('y', '32');
    text.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
    text.setAttribute('font-size', '28');
    text.setAttribute('font-weight', 'bold');
    text.setAttribute('fill', '#fff');
    text.setAttribute('text-anchor', 'middle');
    text.textContent = id.replace('floor', 'Floor ');
    text.style.pointerEvents = 'none';

    svgBtn.appendChild(rect);
    svgBtn.appendChild(text);

    // Click handler
    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      highlightFloor(id);
    };

    // Hover handlers
    const handleMouseOver = () => {
      if (selectedFloor.value !== id) {
        rect.setAttribute('fill', 'rgba(107, 60, 60, 0.95)');
        rect.style.filter = 'drop-shadow(0 0 5px rgba(255, 100, 100, 0.4))';
      }
    };

    const handleMouseOut = () => {
      if (selectedFloor.value !== id) {
        rect.setAttribute('fill', 'rgba(74, 42, 42, 0.9)');
        rect.style.filter = 'none';
      }
    };

    svgBtn.addEventListener('click', handleClick);
    svgBtn.addEventListener('touchstart', handleClick, { passive: false });
    svgBtn.addEventListener('mouseover', handleMouseOver);
    svgBtn.addEventListener('mouseout', handleMouseOut);

    eventListeners.push(
      { element: svgBtn, type: 'click', listener: handleClick },
      { element: svgBtn, type: 'touchstart', listener: handleClick },
      { element: svgBtn, type: 'mouseover', listener: handleMouseOver },
      { element: svgBtn, type: 'mouseout', listener: handleMouseOut }
    );

    parentGroup.appendChild(svgBtn);
  });

  // Click outside to deselect
  svgElement.addEventListener('click', (e) => {
    const isFloorButton = floorIds.some(id => {
      const btn = svgDoc.getElementById(`${id}_btn`);
      return btn && (e.target === btn || btn.contains(e.target));
    });
    if (!isFloorButton) {
      clearSelection();
    }
  });
};

// Initialize SVG
const initializeSvg = async (svgDoc, svgElement) => {
  // Set viewBox if missing
  if (!svgElement.getAttribute('viewBox')) {
    svgElement.setAttribute('viewBox', '0 0 5000 5000');
  }

  // Hide all floor areas initially
  floorIds.forEach(id => {
    const area = svgDoc.getElementById(`${id}_area`);
    if (area) {
      area.style.display = 'none';
    }
  });

  // Initialize pan-zoom
  panZoom = svgPanZoom(svgElement, {
    zoomEnabled: true,
    panEnabled: true,
    controlIconsEnabled: false,
    mouseWheelZoomEnabled: true,
    dblClickZoomEnabled: true,
    minZoom: 0.3,
    maxZoom: 10,
    fit: true,
    center: true,
  });

  // Create floor buttons
  createFloorButtons(svgDoc, svgElement);

  // Center and fit
  await nextTick();
  addTimeout(() => {
    if (panZoom) {
      panZoom.resize();
      panZoom.fit();
      panZoom.center();
    }
  }, 100);
};

// Handle SVG Load
const handleSvgLoad = async () => {
  if (initialized) return;

  try {
    await waitForDimensions(viewportRef.value);
    await new Promise(resolve => setTimeout(resolve, 200));

    const svgObj = svgObjectRef.value;
    const svgDoc = svgObj?.contentDocument;
    const svgEl = svgDoc?.querySelector('svg');

    if (!svgEl) throw new Error('SVG element not found');

    await initializeSvg(svgDoc, svgEl);
    initialized = true;
    
    addTimeout(() => {
      isLoading.value = false;
    }, 300);

  } catch (error) {
    console.error('Failed to load building:', error);
    isLoading.value = false;
  }
};

// Control Handlers
const handleZoomIn = () => {
  if (panZoom) {
    panZoom.zoomIn();
    zoomInActive.value = true;
    addTimeout(() => { zoomInActive.value = false; }, 300);
  }
};

const handleZoomOut = () => {
  if (panZoom) {
    panZoom.zoomOut();
    zoomOutActive.value = true;
    addTimeout(() => { zoomOutActive.value = false; }, 300);
  }
};

const handleReset = () => {
  if (panZoom) {
    panZoom.resetZoom();
    panZoom.fit();
    panZoom.center();
    resetActive.value = true;
    addTimeout(() => { resetActive.value = false; }, 300);
  }
  clearSelection();
};

// Resize Handler
const handleResize = () => {
  if (panZoom && initialized && !isLoading.value) {
    try {
      panZoom.resize();
      panZoom.fit();
      panZoom.center();
    } catch (error) {
      console.warn('Resize error:', error);
    }
  }
};

// Cleanup
const cleanup = () => {
  initialized = false;
  clearAllTimeouts();

  if (resizeObs) {
    resizeObs.disconnect();
    resizeObs = null;
  }

  if (panZoom) {
    try {
      panZoom.destroy();
    } catch (error) {
      console.warn('Error destroying panZoom:', error);
    }
    panZoom = null;
  }

  eventListeners.forEach(({ element, type, listener }) => {
    try {
      element.removeEventListener(type, listener);
    } catch (error) {
      console.warn('Error removing listener:', error);
    }
  });
  eventListeners = [];

  window.removeEventListener('resize', handleResize);
};

// Lifecycle
onMounted(() => {
  window.addEventListener('resize', handleResize);
  
  if (wrapperRef.value) {
    resizeObs = new ResizeObserver(handleResize);
    resizeObs.observe(wrapperRef.value);
  }
});

onBeforeUnmount(() => {
  cleanup();
});

defineExpose({ handleReset });
</script>

<style scoped>
.building-overview-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background: transparent;
  overflow: hidden;
}

/* ==================== Loading Screen ==================== */
.loading-screen {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(248, 250, 252, 0.96) 100%
  );
  backdrop-filter: blur(20px);
  z-index: 100;
  border-radius: 12px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.loading-icon {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  position: absolute;
  inset: -10px;
  border: 3px solid transparent;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

/* ==================== SVG Viewport ==================== */
.svg-viewport {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(59, 130, 246, 0.1);
  min-height: 0;
}

.svg-content {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.svg-content.ready {
  opacity: 1;
}

/* ==================== Floating Controls ==================== */
.floating-controls {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 12px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(248, 250, 252, 0.95) 100%
  );
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  z-index: 50;
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.control-btn:hover {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
}

.control-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
  transform: scale(0.95);
}

/* ==================== Floor Info Card ==================== */
.floor-info-card {
  position: absolute;
  top: 1rem;
  left: 1rem;
  min-width: 280px;
  max-width: 350px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(16px);
  z-index: 60;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.info-header h3 {
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.info-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(59, 130, 246, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.info-close:hover {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  transform: rotate(90deg);
}

.info-divider {
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(59, 130, 246, 0.2) 50%, 
    transparent 100%
  );
  margin-bottom: 1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(59, 130, 246, 0.1);
}

.info-row.total {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
  border-color: rgba(59, 130, 246, 0.2);
}

.info-row .label {
  color: #475569;
  font-size: 0.9375rem;
  font-weight: 500;
}

.info-row.total .label {
  color: #1e293b;
  font-weight: 600;
}

.info-row .value {
  color: #3b82f6;
  font-size: 1.125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* ==================== Transitions ==================== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.info-slide-enter-active,
.info-slide-leave-active {
  transition: all 0.3s ease;
}

.info-slide-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.info-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* ==================== Responsive - Tablet ==================== */
@media (max-width: 1024px) {
  .floor-info-card {
    min-width: 260px;
    max-width: 320px;
    padding: 1.125rem;
  }

  .info-header h3 {
    font-size: 1.125rem;
  }

  .info-row {
    padding: 0.5rem 0.75rem;
  }

  .info-row .label {
    font-size: 0.875rem;
  }

  .info-row .value {
    font-size: 1rem;
  }

  .control-btn {
    width: 34px;
    height: 34px;
  }
}

/* ==================== Responsive - Mobile ==================== */
@media (max-width: 768px) {
  .floor-info-card {
    top: 0.75rem;
    left: 0.75rem;
    right: 0.75rem;
    min-width: auto;
    max-width: none;
    padding: 1rem;
  }

  .info-header h3 {
    font-size: 1.0625rem;
  }

  .info-close {
    width: 26px;
    height: 26px;
  }

  .info-row {
    padding: 0.5rem 0.625rem;
  }

  .info-row .label {
    font-size: 0.8125rem;
  }

  .info-row .value {
    font-size: 0.9375rem;
  }

  .floating-controls {
    bottom: 0.75rem;
    padding: 6px 10px;
    gap: 0.375rem;
  }

  .control-btn {
    width: 32px;
    height: 32px;
  }

  .loading-icon {
    width: 70px;
    height: 70px;
  }

  .loading-text {
    font-size: 1rem;
  }
}

/* ==================== Responsive - Small Mobile ==================== */
@media (max-width: 480px) {
  .floor-info-card {
    top: 0.5rem;
    left: 0.5rem;
    right: 0.5rem;
    padding: 0.875rem;
  }

  .info-header h3 {
    font-size: 1rem;
  }

  .info-close {
    width: 24px;
    height: 24px;
  }

  .info-content {
    gap: 0.625rem;
  }

  .info-row {
    padding: 0.5rem;
  }

  .info-row .label {
    font-size: 0.75rem;
  }

  .info-row .value {
    font-size: 0.875rem;
  }

  .floating-controls {
    bottom: 0.5rem;
    padding: 5px 8px;
    gap: 0.25rem;
  }

  .control-btn {
    width: 30px;
    height: 30px;
  }

  .loading-icon {
    width: 60px;
    height: 60px;
  }

  .loading-text {
    font-size: 0.9375rem;
  }
}

/* ==================== Touch Devices ==================== */
@media (hover: none) and (pointer: coarse) {
  .control-btn {
    min-width: 40px;
    min-height: 40px;
  }
}
</style>